import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import * as AssemblyModels from 'assembly/AssemblyModels';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, ANALYTICS_MODULE, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';
import {BulkAssemblyBanner} from 'molbio/bulk-assembly/Banners';
import {
  CLONING_BULK_ASSEMBLY_METHOD_TO_DEFAULT_METHOD_DATA,
  isConvertedAssemblyType,
  LEGACY_ASSEMBLY_TYPE_TO_BULK_ASSEMBLY_METHOD,
  PreviousTool,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {handleNewCloningBulkAssemblyClick} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import notifications from 'notifications';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';
import Modal from 'ui-library/modals/Modal';

interface CreateAssemblyModalProps {
  onRequestClose: () => void;
}

export function CreateAssemblyModal(props: CreateAssemblyModalProps): JSX.Element {
  const [assemblyType, setAssemblyType] = useState<ASSEMBLY_TYPE | null>(null);

  const assemblyOptions = [
    {
      value: ASSEMBLY_TYPE.DIGEST,
      name: <span className={css(styles.optionText)}>Digest and Ligate</span>,
      description: 'Simple restriction-enzyme based cloning.',
    },
    {
      value: ASSEMBLY_TYPE.GIBSON,
      name: <span className={css(styles.optionText)}>Gibson</span>,
      description: (
        <>
          Homology-based, scarless, single-tube reaction assembly - see{' '}
          <a href="http://www.ncbi.nlm.nih.gov/pubmed/19363495" target="_blank" rel="noopener noreferrer">
            Gibson 2009
          </a>
          .
        </>
      ),
    },
    {
      value: ASSEMBLY_TYPE.GOLDEN_GATE,
      name: <span className={css(styles.optionText)}>Golden Gate</span>,
      description: (
        <>
          Quasi-scarless, single-tube reaction assembly - see{' '}
          <a
            href="http://www.plosone.org/article/info%3Adoi%2F10.1371%2Fjournal.pone.0003647"
            target="_blank"
            rel="noopener noreferrer"
          >
            Engler 2008
          </a>
          .
        </>
      ),
    },
  ];

  return (
    <Modal
      title="Pick Assembly Strategy"
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          text: 'Start',
          submit: true,
          disabled: assemblyType == null,
          onClick: () => {
            analytics.track('assembly', 'start_click', {
              product: ANALYTICS_MODULE.MOLBIO,
              assemblyType,
            });
            const assembly = new AssemblyModels.Assembly({method: assemblyType});
            assembly
              .save()
              .done(() => AssemblyModels.assemblies.add(assembly))
              .fail(notifications.notifyError);
            props.onRequestClose();
          },
          style: ButtonStyle.SECONDARY,
          dataTestAttrs: {component: 'CreateAssemblyModal', element: 'startButton'},
        },
        {
          text: 'Cancel',
          cancel: true,
        },
      ]}
      dataTestAttrs={{component: 'CreateAssemblyModal', element: 'root'}}
    >
      <div className={css(styles.optionsContainer)}>
        <RadioButtonGroup
          selectedValue={assemblyType}
          options={assemblyOptions.map((assemblyOption) => ({
            label: assemblyOption.name,
            secondaryLabel: assemblyOption.description,
            value: assemblyOption.value,
            dataTestAttrs: {
              component: 'CreateAssemblyModal',
              element: 'input',
              key: `assemblyOptionInput-${assemblyOption.value}`,
            },
          }))}
          onChange={(assemblyType: ASSEMBLY_TYPE) => {
            analytics.track('assembly', 'select_assembly_method', {
              product: ANALYTICS_MODULE.MOLBIO,
              assemblyMethod: assemblyType,
            });
            setAssemblyType(assemblyType);
          }}
          title=""
        />
      </div>
      {BenchlingConfig.MOLBIO_REDESIGNED_CLONING_FLOW &&
        assemblyType != null &&
        isConvertedAssemblyType(assemblyType) && (
          <BulkAssemblyBanner
            actionOrigin={ActionOrigin.MOLBIO_ASSEMBLY_WIZARD_MODAL}
            onClick={() => {
              analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLICKED_REDESIGNED_BULK_ASSEMBLY_LINK, {
                actionOrigin: ActionOrigin.MOLBIO_ASSEMBLY_WIZARD_MODAL,
              });
              handleNewCloningBulkAssemblyClick(
                {
                  previousTool: PreviousTool.ASSEMBLY_WIZARD,
                  initialMethodData:
                    CLONING_BULK_ASSEMBLY_METHOD_TO_DEFAULT_METHOD_DATA[
                      LEGACY_ASSEMBLY_TYPE_TO_BULK_ASSEMBLY_METHOD[assemblyType]
                    ],
                },
                ActionOrigin.MOLBIO_ASSEMBLY_WIZARD_MODAL
              );
              props.onRequestClose();
            }}
          />
        )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  optionsContainer: {
    width: '50%',
    margin: 'auto',
  },
  optionText: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
});
